/**
 * CustomerChip - Individual customer chip with remove functionality
 */

import { Cancel, WarningAmber } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

import {
  EVOTRUX_BLUE,
  EVOTRUX_BORDER_GREY,
  EVOTRUX_BORDER_GREY_HOVER,
  EVOTRUX_WARNING_AMBER,
  EVOTRUX_WARNING_AMBER_LIGHT,
} from "constants/index";

import { CUSTOMER_TYPES } from "../utils";

import type { CustomerSelectionItem } from "../types";

// Color constants for consistent theming
const COLORS = {
  primary: EVOTRUX_BLUE,
  primaryLight: "#e3f2fd",
  border: EVOTRUX_BORDER_GREY,
  text: "#616161",
  background: "#f5f5f5",
  iconSecondary: "#9CA3AF",
} as const;

// Internal customers keep their underlying account when removed from the
// network (only the partnership is deleted), so they still receive
// notifications if kept. External customers have no account to fall back on
// -- removal hard-deletes their record, so keeping the chip cannot restore
// notifications for them, unlike internal customers.
const REMOVED_FROM_NETWORK_TOOLTIP_INTERNAL =
  "This customer was removed from your network since this template was saved. " +
  "They'll still be notified unless you remove them from this shipment.";
const REMOVED_FROM_NETWORK_TOOLTIP_EXTERNAL =
  "This customer was removed from your network since this template was saved, " +
  "and their contact info can't be recovered. They will not be notified -- " +
  "remove this chip, or add them to your network again first.";
// A disabled customer is still a partnered network customer -- unlike a
// removed one, nothing about the relationship changed -- but their account
// being disabled means they won't actually receive notifications, the same
// silent gap this warning styling exists to surface.
const DISABLED_ACCOUNT_TOOLTIP =
  "This customer's account has been disabled. They're still in your network, " +
  "but won't receive notifications for this shipment unless their account is " +
  "re-enabled.";

type CustomerWarningReason = "removedFromNetwork" | "disabledAccount" | null;

function getWarningReason(item: CustomerSelectionItem): CustomerWarningReason {
  if (item.type === CUSTOMER_TYPES.EMAIL) {
    return null;
  }
  if (item.isRemovedFromNetwork) {
    return "removedFromNetwork";
  }
  if (item.type === CUSTOMER_TYPES.INTERNAL && item.isDisabled) {
    return "disabledAccount";
  }
  return null;
}

function getWarningTooltip(
  item: CustomerSelectionItem,
  reason: CustomerWarningReason,
): string {
  if (reason === "disabledAccount") {
    return DISABLED_ACCOUNT_TOOLTIP;
  }
  return item.type === CUSTOMER_TYPES.INTERNAL
    ? REMOVED_FROM_NETWORK_TOOLTIP_INTERNAL
    : REMOVED_FROM_NETWORK_TOOLTIP_EXTERNAL;
}

interface CustomerChipProps {
  item: CustomerSelectionItem;
  onRemove: (item: CustomerSelectionItem) => void;
  getCustomerKey: (item: CustomerSelectionItem) => string;
}

export function CustomerChip({
  item,
  onRemove,
  getCustomerKey,
}: CustomerChipProps) {
  const getChipLabel = (customer: CustomerSelectionItem): string => {
    if (customer.type === CUSTOMER_TYPES.INTERNAL) {
      return `${customer.first_name} ${customer.last_name} (${customer.email})`;
    }
    if (customer.type === CUSTOMER_TYPES.EXTERNAL) {
      return customer.first_name && customer.last_name
        ? `${customer.first_name} ${customer.last_name} (${customer.email})`
        : customer.email;
    }
    return customer.email;
  };

  const isEvotruxUser = (customer: CustomerSelectionItem): boolean =>
    customer.type === CUSTOMER_TYPES.INTERNAL;

  const warningReason = getWarningReason(item);
  const hasWarning = warningReason !== null;
  const isBlue = isEvotruxUser(item);

  // Disabled accounts get a muted gray -- matching how disabled controls read
  // elsewhere in the app (text.disabled / Mui-disabled) -- rather than the
  // removed-from-network amber, so the two warning states read as visually
  // distinct at a glance without borrowing the error-red reserved for actual
  // failures (a lapsed subscription, a failed invite) elsewhere in the app.
  const warningColor =
    warningReason === "disabledAccount"
      ? EVOTRUX_BORDER_GREY_HOVER
      : EVOTRUX_WARNING_AMBER;
  // Pale enough to sit alongside the other two chip backgrounds (blue at
  // #e3f2fd, amber at EVOTRUX_WARNING_AMBER_LIGHT) instead of the medium
  // LIGHT_GREY, which read too close to the border/text gray to stay legible.
  const warningBgColor =
    warningReason === "disabledAccount"
      ? COLORS.background
      : EVOTRUX_WARNING_AMBER_LIGHT;

  const getChipColors = (): { border: string; text: string; bg: string } => {
    if (hasWarning) {
      return { border: warningColor, text: warningColor, bg: warningBgColor };
    }
    if (isBlue) {
      return {
        border: COLORS.primary,
        text: COLORS.primary,
        bg: COLORS.primaryLight,
      };
    }
    return { border: COLORS.text, text: COLORS.text, bg: COLORS.background };
  };

  const { border: borderColor, text: textColor, bg: bgColor } = getChipColors();
  const iconColor = COLORS.iconSecondary;

  const chip = (
    <span
      key={getCustomerKey(item)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        borderRadius: "20px",
        padding: "3px 5px 3px 10px",
        fontSize: "14px",
        maxWidth: "100%",
        border: `1px solid ${borderColor}`,
        color: textColor,
        backgroundColor: bgColor,
        whiteSpace: "nowrap",
      }}
    >
      {hasWarning && (
        <WarningAmber
          data-testid={
            warningReason === "disabledAccount"
              ? "disabled-account-badge"
              : "removed-from-network-badge"
          }
          sx={{ fontSize: 16, color: warningColor }}
        />
      )}
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: 500,
          fontFamily: "Quicksand, sans-serif",
          color: "inherit",
        }}
      >
        {getChipLabel(item)}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item);
        }}
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <Cancel
          sx={{
            fontSize: 18,
            color: iconColor,
            "&:hover": {
              color: "#78909C",
            },
          }}
        />
      </button>
    </span>
  );

  return hasWarning ? (
    <Tooltip title={getWarningTooltip(item, warningReason)}>{chip}</Tooltip>
  ) : (
    chip
  );
}
